import axios from "axios";
import humps from "humps";
import { APP_BASE_API_URL } from "@/config/app.config";
import { useUserStore } from "@/stores/user.store";
import vuetify from "@/plugins/vuetify";
import qs from "qs";
import { getSignal } from "./requestManager.util";

const request = axios.create({
  baseURL: APP_BASE_API_URL,
});

// Request interceptor: Convert request data from camelCase to snake_case
request.interceptors.request.use(
  (config) => {
    const userStore = useUserStore();
    const currentLanguage = vuetify?.locale?.current?.value || "en";

    config.headers = {
      "X-HH-Language": currentLanguage,
      "Content-Type": "application/json",
      ...(userStore.token ? { Authorization: `Bearer ${userStore.token}` } : {}),
      ...(config.headers || {}),
    };

    // If data is FormData, let browser set the correct content-type
    const isFormData = config.data instanceof FormData;
    if (!isFormData) {
      config.headers["Content-Type"] = "application/json";
    }

    if (config.method === "get") {
      config.paramsSerializer = function (params) {
        return qs.stringify(params, { arrayFormat: "repeat" });
      };
      if (config.params) {
        config.params = humps.decamelizeKeys(config.params);
      }
    } else if (config.data && !isFormData) {
      config.data = humps.decamelizeKeys(config.data);
    }

    const ua = window.navigator.userAgent;
    const msie = ua.indexOf("MSIE ");
    const trident = ua.indexOf("Trident/");
    if (msie > 0 || trident > 0) {
      config.headers.common["Pragma"] = "no-cache";
    }

    // inject AbortSignal if provided
    const signal = getSignal(config.url);
    if (signal) {
      config.signal = signal;
    }

    return config;
  },
  (err) => Promise.reject(err),
);

// Response interceptor: Convert response data from snake_case to camelCase
request.interceptors.response.use(
  (response) => {
    if (response.request.responseType === "blob") {
      return response;
    }
    return humps.camelizeKeys(response.data);
  },
  (error) => {
    const userStore = useUserStore();
    if (error?.response?.status === 401) {
      userStore.logOut();
      window.location.replace("/unauthorized-401");
    }
    if (error?.response?.status === 403) {
      window.location.replace("/access-restricted-403");
    }
    if (error?.response?.data) return Promise.reject(humps.camelizeKeys(error.response.data));
    return Promise.reject(error);
  },
);

export default request;
